"use client";

import type { Database } from "@pex/database/types";
import { createBrowserClient } from "@supabase/ssr";

// Use global to persist across module reloads in development
const globalForSupabase = globalThis as unknown as {
  supabaseBrowserClient:
    | ReturnType<typeof createBrowserClient<Database>>
    | undefined;
};

/**
 * Get cookie options for cross-subdomain authentication
 * Must match server-side getCookieOptions() in middleware.ts
 *
 * NEXT_PUBLIC_COOKIE_DOMAIN must be set per environment:
 *   Local:      .pex.local
 *   Staging:    .staging.projectexodus.net
 *   Production: .projectexodus.net
 */
function getCookieOptions() {
  const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN || ".pex.local";
  const isSecure = cookieDomain !== ".pex.local";

  return {
    domain: cookieDomain,
    secure: isSecure,
    sameSite: "lax" as const,
    path: "/",
    maxAge: 60 * 60 * 24 * 30, // 30 days
    httpOnly: false, // Not required - tokens are designed to be accessible
  };
}

export function getSupabaseBrowserClient() {
  if (globalForSupabase.supabaseBrowserClient) {
    return globalForSupabase.supabaseBrowserClient;
  }

  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const publishableKey = process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY;

  if (!(url && publishableKey)) {
    throw new Error(
      "Missing Supabase environment variables. Please set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY."
    );
  }

  const client = createBrowserClient<Database>(url, publishableKey, {
    cookieOptions: getCookieOptions(),
  });

  // No refresh-failure listener is needed here. In @supabase/auth-js v2, when a
  // token refresh fails with a non-retryable error (401, 400, 429), GoTrue calls
  // _removeSession() internally and emits SIGNED_OUT automatically — there is no
  // window where TOKEN_REFRESHED fires without a session. Apps that need to redirect
  // on session loss should listen for the SIGNED_OUT event directly.

  globalForSupabase.supabaseBrowserClient = client;
  return client;
}
